import React, { SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AutocompleteState } from 'component/Layout/Autocomplete/types';

import {
    diffSets,
    mergeSets,
    toggleValueInSet,
} from 'utility/Toggle/toggleValueInSet';

import { Airport } from 'types/legacy/LastSearch';
import { Iata } from 'types/Search/airport';
import { AirportValue } from 'types/Search/filter';

import { ListByCountries } from '../__config/Airports';
import { getAirportLabelList, getRegionIataList } from '../__helper/airport';
import { AirportAcItemList, Country, Region } from '../types';

interface HookProps {
    action: {
        setResults: (results: AirportAcItemList[]) => void;
        setValue: (value: AirportValue<Iata>) => void;
        config: { setThreshold: (threshold: number) => void };
    };
    airportState: AutocompleteState<AirportAcItemList, Iata>;
    onToggle: (isActive: boolean) => void;
    setInputValue: React.Dispatch<SetStateAction<string>>;
    setLastValidInput: React.Dispatch<SetStateAction<string>>;
    onSubmitValue: (value: AirportValue<Iata>) => void;
}

export const useClickCallbacks = ({
    action,
    setLastValidInput,
    airportState,
    setInputValue,
    onToggle,
    onSubmitValue,
}: HookProps) => {
    const { t } = useTranslation();
    const onClick = useCallback(
        (currentIataList: Set<Iata>, isChecked: boolean) => {
            const newList = !isChecked
                ? mergeSets(airportState.currentValue, currentIataList) //add values
                : diffSets(airportState.currentValue, currentIataList); // remove values
            action.setValue(newList);
            setLastValidInput(getAirportLabelList(newList, t));
        },
        [action, airportState.currentValue, setLastValidInput, t]
    );

    const onGroupClick = useCallback(
        (country: Country, isChecked: boolean, region?: Region) => {
            const currentIataList =
                region !== undefined
                    ? getRegionIataList(region, country)
                    : ListByCountries[country].iataCodeList;
            onClick(currentIataList, isChecked);
        },
        [onClick]
    );

    const onCheckboxClick = useCallback(
        (entry: Iata) => {
            const newList = toggleValueInSet(airportState.currentValue, entry);
            action.setValue(newList);
            setLastValidInput(getAirportLabelList(newList, t));
        },
        [action, airportState.currentValue, setLastValidInput, t]
    );

    const onChipClick = useCallback(
        (code?: Iata, country?: Country) => {
            let toDelete: Set<Iata>;
            if (code) {
                toDelete = new Set([code]);
            } else if (country) {
                toDelete = ListByCountries[country].iataCodeList;
            } else {
                return;
            }
            const stateIataList = diffSets(airportState.currentValue, toDelete);
            action.setValue(stateIataList);
            setLastValidInput(getAirportLabelList(stateIataList, t));
        },
        [action, airportState.currentValue, setLastValidInput, t]
    );

    const onDeleteClick = useCallback(() => {
        action.setValue(new Set());
        setLastValidInput(getAirportLabelList(new Set(), t));
    }, [action, setLastValidInput, t]);

    const onLastSearchesClick = useCallback(
        (entry: Airport) => {
            const iataList = new Set(entry.id.split(',') as Iata[]);
            const label = getAirportLabelList(iataList, t);
            action.setValue(iataList);
            setInputValue(label);
            setLastValidInput(label);
            onSubmitValue(iataList);
            onToggle(false);
        },
        [action, onSubmitValue, onToggle, setInputValue, setLastValidInput, t]
    );

    return {
        onDeleteClick,
        onGroupClick,
        onClick,
        onCheckboxClick,
        onChipClick,
        onLastSearchesClick,
    };
};
